import { useContext, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ValueAndWeightPerExercise } from './ValueAndWeightPerExercise';
import { TrainingStatsTab } from './TrainingStatsTab';
import { isTrainerUser } from '@/utils/user';
import { useParams } from 'react-router-dom';
import { AppContext } from '@/contexts/app.context';
import { useTraineeProfileQuery, useTrainingHistoryByUserQuery, useTrainingHistoryQuery } from '@/hooks/query-hooks';
import { DefaultContainer } from '@/components/DefaultContainer';
import { Delete, Edit, FitnessCenter, QueryStats } from '@mui/icons-material';
import { useDeleteTrainingHistoryEntry, useUpdateTrainingHistoryEntry } from '@/hooks/training-history-mutation.hooks';
import { TrainingHistoryEntry } from '@/generated';
import { EditTrainingHistoryDialog } from './EditTrainingHistoryDialog';
import { enqueueSnackbar } from 'notistack';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  label: string;
  value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, label, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`training-history-panel-${index}`} aria-label={label} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

export const TrainingHistoryScreen = () => {
  const { state } = useContext(AppContext);
  const { userId } = useParams();
  const { trainingHistory } = useTrainingHistoryQuery(userId === undefined);
  const { trainingHistoryByUser } = useTrainingHistoryByUserQuery(userId);
  const [selectedTab, setSelectedTab] = useState(0);
  const displayedTrainingHistory = userId ? trainingHistoryByUser : trainingHistory;
  const { profile } = useTraineeProfileQuery(userId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const deleteTrainingHistoryEntryMutation = useDeleteTrainingHistoryEntry();
  const updateTrainingHistoryEntryMutation = useUpdateTrainingHistoryEntry();
  const [selectedTrainingStatsTabEntryId, setSelectedTrainingStatsTabEntryId] = useState<string>('');
  const [selectedTrainingStatsTabEntry, setSelectedTrainingStatsTabEntry] = useState<TrainingHistoryEntry>();

  return (
    <DefaultContainer>
      <DeleteDialog
        open={deleteDialogOpen}
        onDeletePressed={async () => {
          if (selectedTrainingStatsTabEntryId) await deleteTrainingHistoryEntryMutation.mutateAsync(selectedTrainingStatsTabEntryId);
          setSelectedTrainingStatsTabEntryId('');
          setSelectedTrainingStatsTabEntry(undefined);
        }}
        setOpen={(open) => setDeleteDialogOpen(open)}
      />
      <EditTrainingHistoryDialog
        entry={selectedTrainingStatsTabEntry}
        open={editDialogOpen}
        saving={updateTrainingHistoryEntryMutation.isPending}
        onClose={() => setEditDialogOpen(false)}
        onSave={async (entry) => {
          try {
            await updateTrainingHistoryEntryMutation.mutateAsync(entry);
            setEditDialogOpen(false);
            enqueueSnackbar('Training history entry updated.', { variant: 'success' });
          } catch {
            enqueueSnackbar('Training history entry could not be updated.', { variant: 'error' });
          }
        }}
      />
      <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
        <Grid container gap={2} direction={'row'} sx={{ alignContent: 'center', alignItems: 'center' }}>
          <Tooltip title="Training Stats">
            <IconButton
              aria-label="Show training stats"
              aria-pressed={selectedTab === 0}
              color={selectedTab === 0 ? 'primary' : 'default'}
              sx={{ backgroundColor: selectedTab === 0 ? 'action.selected' : 'transparent' }}
              onClick={() => {
                setSelectedTab(0);
                setSelectedTrainingStatsTabEntryId('');
                setSelectedTrainingStatsTabEntry(undefined);
              }}
            >
              <QueryStats />
            </IconButton>
          </Tooltip>
          <Tooltip title="Exercise Stats">
            <IconButton
              aria-label="Show exercise stats"
              aria-pressed={selectedTab === 1}
              color={selectedTab === 1 ? 'primary' : 'default'}
              sx={{ backgroundColor: selectedTab === 1 ? 'action.selected' : 'transparent' }}
              onClick={() => {
                setSelectedTab(1);
                setSelectedTrainingStatsTabEntryId('');
                setSelectedTrainingStatsTabEntry(undefined);
              }}
            >
              <FitnessCenter />
            </IconButton>
          </Tooltip>
          <Tooltip title={'Delete Selected Entry'}>
            <IconButton
              disabled={!selectedTrainingStatsTabEntryId}
              onClick={() => {
                setDeleteDialogOpen(true);
              }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Selected Entry">
            <span>
              <IconButton disabled={!selectedTrainingStatsTabEntry} onClick={() => setEditDialogOpen(true)} aria-label="Edit selected training history entry">
                <Edit />
              </IconButton>
            </span>
          </Tooltip>
          {isTrainerUser(state?.userProfile) && userId && (
            <Stack direction="row" sx={{ ml: 'auto', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
              <Avatar />
              <Typography noWrap>Viewing User Stats for {profile?.username}</Typography>
            </Stack>
          )}
        </Grid>
      </Paper>
      <Stack>
        <CustomTabPanel value={selectedTab} index={0} label="Training Stats">
          <TrainingStatsTab
            setSelectedEntryId={setSelectedTrainingStatsTabEntryId}
            setSelectedEntry={setSelectedTrainingStatsTabEntry}
            trainingHistory={displayedTrainingHistory}
          />
        </CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={1} label="Exercise Stats">
          <ValueAndWeightPerExercise trainingHistory={displayedTrainingHistory} />
        </CustomTabPanel>
      </Stack>
    </DefaultContainer>
  );
};

function DeleteDialog(props: { open: boolean; setOpen: (open: boolean) => void; onDeletePressed: () => void }) {
  const { open, setOpen, onDeletePressed } = props;

  return (
    <Dialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">{'Delete History Entry?'}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">Really delete this entry?</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setOpen(false);
            onDeletePressed();
          }}
        >
          Delete
        </Button>
        <Button onClick={() => setOpen(false)} autoFocus>
          Keep It
        </Button>
      </DialogActions>
    </Dialog>
  );
}
